<template>
  <SectionTemplate title="常见问题（FAQs）">
    <XFaq :faq-list="faqList" />
  </SectionTemplate>
</template>

<script setup lang="ts">
import XFaq from '@/design/widgets/faq/faq.vue';
import { useFaqHelper } from '@/design/widgets/faq/useFaqHelper';
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import { openCustomerService } from '~/utils/kefu';

const handleContactCustomer = () => {
  openCustomerService();
};

// 监听页面点击事件
onMounted(() => {
  document.addEventListener('click', handleClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClick);
});

// 处理点击事件，支持自定义链接和联系方式
const handleClick = (event: Event) => {
  const target = event.target as HTMLElement;
  if (target.tagName === 'A' && target.dataset.action) {
    event.preventDefault();
    handleContactCustomer();
  }
};

// 使用 FAQ 辅助工具
const { getStrong } = useFaqHelper([]);

// FAQ 数据列表
const faqList = [
  {
    question: '有言支持哪些格式的PPT文件？上传有什么限制吗？',
    answer: `有言目前完美支持市面上主流的<span style="white-space: nowrap">${getStrong('PPT')}和${getStrong('PPTX')}</span>格式文件上传。你可以直接上传本地的演示文稿，无需任何预处理。对于文件大小和页数，我们的系统能够稳定处理绝大多数常规企业级文档，即使是包含大量高清图片的复杂PPT也能流畅解析，确保你的存量内容都能被高效利用。`,
    isOpen: false,
  },
  {
    question: '我PPT里精心设计的动画和图表，转换成视频后还会保留吗？',
    answer: `关于PPT动画效果，有言目前主要聚焦于PPT的静态内容展示。上传PPT后，AI会智能提取您PPT中的文字、图表、图片等核心内容，并以静态形式呈现在视频中。PPT中预设的动画效果（如飞入、淡出等）在转换过程中不会保留。`,
    isOpen: false,
  },
  {
    question: '生成这样一个3D数字人讲解视频，大概需要多长时间？',
    answer: `速度非常快！转换时间主要取决于你PPT的页数和视频的复杂程度。通常情况下，一个10页左右的演示文稿，只需${getStrong('几分钟')}就能生成预览视频。相比传统需要拍摄、剪辑、后期数天的制作周期，有言将效率提升了10倍以上，真正实现“小时级”产出。`,
    isOpen: false,
  },
  {
    question: '视频里的数字人形象是固定的吗？我可以选择或定制吗？',
    answer: `当然可以！这是有言的核心优势之一。我们内置了${getStrong('3000+')} 个超写实的3D数字人形象，覆盖不同年龄、族裔和职业风格。你可以在第二步“选择人物形象”时自由筛选，找到最适合你视频风格（如商务、教育、亲和等）的数字人。此外，我们还支持${getStrong('照片克隆')}或${getStrong('企业IP定制')}，让你拥有专属的数字人分身。`,
    isOpen: false,
  },
  {
    question: '我不想用自己的声音配音，平台提供哪些语音选择？口型能对得上吗？',
    answer: `有言提供超过${getStrong('400种')}AI声音，涵盖${getStrong('100多种语言')}和${getStrong('30多种方言')}。你可以根据视频风格选择沉稳、活泼、知性等不同音色。更重要的是，我们自研的TTS技术能实现${getStrong('中文口型同步精度行业领先')}，AI生成的语音会智能驱动数字人的口型和面部表情，确保视频看起来自然真实，彻底告别“音画不同步”的违和感。`,
    isOpen: false,
  },
  {
    question: '生成的视频可以进行后期剪辑吗？比如我想调整某一段的字幕或镜头。',
    answer: [
      `完全可以。有言内置了强大的【视频剪辑和编辑功能】。视频生成后，你无需切换到PR、剪映等外部软件，直接在有言内就能进行${getStrong('精细化剪辑')}。你可以：`,
      [
        `${getStrong('修改字幕')}：调整字体、颜色、位置。`,
        `${getStrong('替换元素')}：单独更换某一页的数字人动作、场景背景甚至语音音色，无需全片重渲。`,
        `${getStrong('调整镜头')}：修改AI自动生成的运镜节奏，打造专属的镜头语言。`,
        `${getStrong('包装优化')}：添加品牌LOGO、片头片尾、背景音乐等，一站式完成专业后期。`,
      ],
    ],
    isOpen: false,
  },
  {
    question: '除了生成视频，有言还能让我的PPT“活”起来与观众互动吗？',
    answer: `这正是有言的独特魅力所在！除了“PPT转视频”，我们还提供了革命性的 ${getStrong('【PPT Speak智能体】')} 功能。你可以将同一个PPT一键发布为一个“可交互的数字人”。观众打开链接，不仅能观看数字人按页讲解，还能随时${getStrong('文字或语音提问')}，数字人会严格依据PPT内的知识库进行精准回答，实现从“单向灌输”到“双向互动”的升级。`,
    isOpen: false,
  },
  {
    question: '我需要制作英文、日文等多语言版本的产品视频，有言能帮忙吗？',
    answer: `能。针对多语种需求，有言提供了全链路AI支持。你可以利用【AI智能脚本生成】功能写好中文脚本，然后使用平台自带的${getStrong('一键翻译')}功能，将脚本转化为目标语言。在生成视频时，直接选择对应的语言音色，AI数字人就会用流利的外语进行讲解，且口型自动匹配，轻松搞定海外市场推广视频。`,
    isOpen: false,
  },
  {
    question: '免费版和付费版的主要区别是什么？如何获得无水印的高清视频？',
    answer: `免费版主要供用户体验流程，提供3款基础数字人形象，生成的视频会带有平台水印。若需用于商业用途，我们提供多种付费升级方案。付费版将${getStrong('去除视频水印')}，解锁${getStrong('3000+')}' 全部数字人形象、${getStrong('400+')} 音色库、${getStrong('1080P/4K')}高清导出以及${getStrong('视频批量生成')}等高级功能，满足企业级专业需求。`,
    isOpen: false,
  },
  {
    question: '如果我在制作过程中遇到问题，或者有特殊的定制需求，怎么办？',
    answer: `有言为企业用户提供全方位的支持服务。你可以在平台内通过帮助中心查找教程，或联系我们的<a data-action="customer">在线客服</a>获取实时帮助。对于企业专属数字人定制、API对接或特定行业的解决方案，我们的销售团队和技术专家会提供一对一的专属服务，确保你的需求得到完美解决。`,
    isOpen: false,
  },
];
</script>
